import { ColumnDef, PaginationState } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Loader, MoreVertical, UserPlus, Users, Shield, UserCheck, UserX } from "lucide-react";
import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import DataTable from "@/components/datatable/Datatable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useModalState from "@/hooks/useModalState";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import * as z from "zod";
import { toast } from "sonner";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getDefaultAvatar } from "@/utils/getDefaultAvatar";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  first_name: z.string().min(2, "First name must be at least 2 characters"),
  last_name: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  password: z.union([
    z.string().min(6, "Password must be at least 6 characters"),
    z.literal(""),
    z.undefined()
  ]).optional(),
  phone: z.preprocess(
    (val) => (val === null || val === undefined ? "" : val),
    z.union([
      z.string().min(10, "Phone number must be at least 10 characters"),
      z.literal("")
    ]).optional()
  ),
  role: z.enum(["Admin", "Technical Support", "President", "Secretary", "Accountant", "Member"], {
    required_error: "Role is required",
  }),
  status: z.enum(["active", "inactive"]),
  profileUrl: z.preprocess(
    (val) => (val === null || val === undefined ? "" : val),
    z.union([z.string(), z.literal("")]).optional()
  ),
});

const systemUserRoles = [
  "Admin",
  "Technical Support",
  "President",
  "Secretary",
  "Accountant",
  "Member",
] as const;

const isProtectedTechnicalSupportUser = (user: any) =>
  user?.role === "Technical Support";

function UserForm({ isOpen, setIsOpen, refetch, record }) {

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      phone: "",
      role: undefined,
      status: "active",
      profileUrl: "",
    },
    values: record
      ? {
        name: record.name || "",
        first_name: record.first_name || "",
        last_name: record.last_name || "",
        email: record.email || "",
        password: "",
        phone: record.phone ?? "",
        role: record.role,
        status: record.status || "active",
        profileUrl: record.profileUrl ?? "",
      }
      : undefined,
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    console.log("🔧 [SystemUser Form] Submitting form with values:", values);
    console.log("🔧 [SystemUser Form] Is update mode?", !!record);
    
    // Prepare payload - clean up empty strings
    const payload: any = {
      name: values.name,
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      role: values.role,
      status: values.status,
    };
    
    // Add optional fields - handle null, undefined, and empty strings
    if (values.phone !== undefined && values.phone !== null) {
      const phoneValue = typeof values.phone === 'string' ? values.phone.trim() : values.phone;
      if (phoneValue) {
        payload.phone = phoneValue;
      } else {
        payload.phone = null; // Explicitly set to null if empty
      }
    }
    if (values.profileUrl !== undefined && values.profileUrl !== null) {
      const profileUrlValue = typeof values.profileUrl === 'string' ? values.profileUrl.trim() : values.profileUrl;
      if (profileUrlValue) {
        payload.profileUrl = profileUrlValue;
      } else {
        payload.profileUrl = null; // Explicitly set to null if empty
      }
    }
    
    // Handle password
    if (record) {
      // For updates, only include password if provided
      if (values.password && values.password.trim()) {
        payload.password = values.password;
      }
    } else {
      // For new users, password is required
      if (!values.password || !values.password.trim()) {
        toast.error("Password is required for new users");
        form.setError("password", {
          message: "Password is required",
        });
        return;
      }
      payload.password = values.password;
    }

    console.log("🔧 [SystemUser Form] Payload to send:", payload);

    const q = record
      ? api.patch(`/users/${record.id}`, payload)
      : api.post("/users", payload);

    try {
      console.log("🔧 [SystemUser Form] Making API request...");
      const response = await q;
      console.log("🔧 [SystemUser Form] API response:", response);
      refetch();
      toast.success(
        record ? "System user updated successfully" : "System user created successfully"
      );
      setIsOpen(false);
      form.reset();
    } catch (e: any) {
      console.error("❌ [SystemUser Form] Error:", e);
      console.error("❌ [SystemUser Form] Error response:", e?.response?.data);
      const errorMessage = e?.response?.data?.message || e?.message || "Failed to save user";
      toast.error(errorMessage);
      const errors = e?.response?.data?.errors || e?.response?.data?.meta?.errors || {};
      Object.keys(errors)?.forEach((field: any) => {
        form.setError(field, {
          message: errors[field],
        });
      });
    }
  }


  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-2xl max-w-[95vw] max-h-[95vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="border-b px-6 py-4 flex-shrink-0 bg-[#009A44]/5">
          <DialogTitle className="text-lg font-semibold text-[#009A44]">
            {record ? "Update System User" : "Add System User"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                console.log("🔧 [SystemUser Form] Form submit event triggered");
                console.log("🔧 [SystemUser Form] Form state:", {
                  isValid: form.formState.isValid,
                  errors: form.formState.errors,
                  isSubmitting: form.formState.isSubmitting,
                  values: form.getValues()
                });
                
                // If form is invalid, show errors
                if (!form.formState.isValid) {
                  console.error("❌ [SystemUser Form] Validation errors:", form.formState.errors);
                  Object.keys(form.formState.errors).forEach((key) => {
                    const error = form.formState.errors[key as keyof typeof form.formState.errors];
                    if (error) {
                      toast.error(`${key}: ${error.message || "Invalid value"}`);
                    }
                  });
                }
                
                form.handleSubmit(onSubmit)(e);
              }}
              className="space-y-6 py-6 px-6"
            >

              {/* User Information Section */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-700 border-b border-[#009A44]/20 pb-2 flex items-center gap-2">
                  <div className="w-1 h-5 bg-[#009A44] rounded-full"></div>
                  User Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">First Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter first name"
                            error={fieldState?.error?.message}
                            className="h-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Last Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter last name"
                            error={fieldState?.error?.message}
                            className="h-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field, fieldState }) => (
                      <FormItem className="col-span-2">
                        <FormLabel className="text-sm font-medium">Full Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter full name"
                            error={fieldState?.error?.message}
                            className="h-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-700 border-b border-[#009A44]/20 pb-2 flex items-center gap-2">
                  <div className="w-1 h-5 bg-[#009A44] rounded-full"></div>
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Enter email address"
                            error={fieldState?.error?.message}
                            className="h-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Phone</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter phone number"
                            error={fieldState?.error?.message}
                            className="h-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Account Settings */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-700 border-b border-[#009A44]/20 pb-2 flex items-center gap-2">
                  <div className="w-1 h-5 bg-[#009A44] rounded-full"></div>
                  Account Settings
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">{record ? "New Password" : "Password"}</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder={record ? "Leave blank to keep current" : "Enter password (min 6 characters)"}
                            error={fieldState?.error?.message}
                            className="h-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                        {!record && (
                          <p className="text-xs text-gray-500 mt-1">Password is required for new users</p>
                        )}
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Status</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger error={fieldState?.error?.message} className="h-10">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Permissions and Assignment */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-700 border-b border-[#009A44]/20 pb-2 flex items-center gap-2">
                  <div className="w-1 h-5 bg-[#009A44] rounded-full"></div>
                  Role Assignment
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Role</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger error={fieldState?.error?.message} className="h-10">
                                <SelectValue placeholder="Select role" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                               {systemUserRoles.map((role) => (
                                 <SelectItem key={role} value={role}>
                                   {role}
                                 </SelectItem>
                               ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                        <p className="text-xs text-gray-500 mt-1">
                          Admin, Technical Support, President, Secretary, and Accountant have full system access
                        </p>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <DialogFooter className="border-t px-6 py-4 mt-6 flex-shrink-0 bg-[#009A44]/5">
                <div className="flex justify-end gap-3 w-full">
                  <Button
                    type="button"
                    variant="outline"
                    size="default"
                    onClick={() => {
                      setIsOpen(false);
                      form.reset();
                    }}
                    className="min-w-[100px]"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={form.formState.isSubmitting}
                    type="submit"
                    size="default"
                    className="text-white font-semibold min-w-[160px]"
                    style={{ 
                      backgroundColor: form.formState.isSubmitting ? '#cccccc' : '#009A44',
                      border: 'none',
                      cursor: form.formState.isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                    onClick={(e) => {
                      console.log("🔧 [SystemUser Form] Button clicked");
                      console.log("🔧 [SystemUser Form] Form state:", {
                        isValid: form.formState.isValid,
                        errors: form.formState.errors,
                        isSubmitting: form.formState.isSubmitting,
                        touchedFields: form.formState.touchedFields
                      });
                    }}
                    onMouseEnter={(e) => {
                      if (!form.formState.isSubmitting) {
                        (e.target as HTMLButtonElement).style.backgroundColor = '#008238';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!form.formState.isSubmitting) {
                        (e.target as HTMLButtonElement).style.backgroundColor = '#009A44';
                      }
                    }}
                  >
                    {form.formState.isSubmitting && (
                      <Loader className="mr-2 h-4 w-4 text-white animate-spin" />
                    )}
                    {record ? "Update System User" : "Add System User"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function SystemUsers() {
  const [recordToEdit, setRecordToEdit] = useState(undefined);

  const columns: ColumnDef<any>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="User" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={row.original.profileUrl || getDefaultAvatar(row.original.id || row.original.email || row.original.name)} />
              <AvatarFallback>{row.original.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <a href="#" className="font-medium hover:underline">
                {row.original.name}
              </a>
              <span className="text-xs text-gray-500">
                {row.original.first_name} {row.original.last_name}
              </span>
            </div>
          </div>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "contact",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Contact" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <span className="text-sm">{row.original.email}</span>
            <span className="text-xs text-gray-500">{row.original.phone || "—"}</span>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: true,
    },
    {
      accessorKey: "role",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Role" />
      ),
      cell: ({ row }) => {
        const role = row.original.role;
        const roleColors = {
          Admin: "bg-[#009A44]/10 text-[#009A44] border-0",
          "Technical Support": "bg-cyan-50 text-cyan-700 border-0",
          President: "bg-red-50 text-red-700 border-0",
          Secretary: "bg-blue-50 text-blue-700 border-0", 
          Accountant: "bg-amber-50 text-amber-700 border-0",
          Member: "bg-gray-100 text-gray-700 border-0"
        };
        
        return (
          <Badge className={roleColors[role] || "bg-gray-100 text-gray-800"}>
            {role}
          </Badge>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.original?.status;
        return (
          <div className="flex items-center gap-3">
            {status && (
              <Badge
                className={`capitalize border-0 ${
                  status === "active"
                    ? "bg-[#009A44]/10 text-[#009A44]"
                    : "bg-red-50 text-red-700"
                }`}
              >
                {status}
              </Badge>
            )}
          </div>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => {
        // Format date nicely
        const date = new Date(row.original.createdAt);
        const formattedDate = new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(date);

        return <div className="text-sm">{formattedDate}</div>;
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: "actions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="" />
      ),
      cell: ({ row }) => {
        const isProtected = isProtectedTechnicalSupportUser(row.original);

        return (
          <div className="flex items-center justify-end mt-1 gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-7 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                {isProtected ? (
                  <DropdownMenuItem disabled>
                    Protected system user
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        setRecordToEdit(row?.original);
                      }}
                    >
                      Edit System User
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        confirmModal.open({ meta: row?.original });
                      }}
                      className="text-red-600"
                    >
                      Delete System User
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const newRecordModal = useModalState();
  const confirmModal = useConfirmModal();

  const recordsQuery = useQuery({
    queryKey: [
      "users",
      {
        search: searchText,
        filter: columnFilters,
        sort: sorting,
        pageIndex,
        pageSize,
      },
    ],
    keepPreviousData: true,
    queryFn: async () => {
      const { data } = await api.get(`/users`, {
        params: {
          page_size: pageSize,
          page: pageIndex + 1,
          ...(searchText && { search: searchText }),
          filters: [
            ...columnFilters.map((e) => {
              return {
                field: e.id,
                operator: "in",
                value: e.value?.map((e) => e?.value || e),
              };
            }),
          ],
        },
      });

      return {
        items: data?.results.map((e) => {
          return {
            ...e,
            role: e.role || e.displayRole,
          };
        }),
        totalPages: data?.totalPages,
      };
    },
  });

  const handleDelete = (record) => {
    confirmModal.setIsLoading(true);
    return api
      .delete(`/users/${record.id}`)
      .then(() => {
        recordsQuery.refetch();
        confirmModal.close();
        toast.success("System user deleted successfully");
      })
      .catch((e) => {
        confirmModal.setIsLoading(false);
        toast.error(e.message);
      });
  };

  return (
    <>
      <ConfirmModal
        title={"Are you sure you want to delete this system user?"}
        description={`This will permanently delete ${confirmModal.meta?.name || 'this system user'} and cannot be undone.`}
        meta={confirmModal.meta}
        onConfirm={(meta) => {
          handleDelete(meta);
        }}
        isLoading={confirmModal.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      <div className="px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">System User Management</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage system users, roles, and permissions
            </p>
          </div>
          <Button
            onClick={() => {
              newRecordModal.open();
            }}
            size="sm"
            className="shadow-sm text-white font-semibold"
            style={{ 
              backgroundColor: '#009A44',
              border: 'none'
            }}
            onMouseEnter={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#008238'}
            onMouseLeave={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#009A44'}
          >
            <UserPlus size={16} className="mr-2" />
            <span>Add System User</span>
          </Button>
        </div>

        {/* Summary Cards */}
        {recordsQuery?.data?.items && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#009A44]/10">
                    <Users className="h-5 w-5 text-[#009A44]" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-2xl font-bold">{recordsQuery.data.items.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-50">
                    <UserCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Active</p>
                    <p className="text-2xl font-bold text-green-600">
                      {recordsQuery.data.items.filter((u: any) => u.status === "active").length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-50">
                    <UserX className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Inactive</p>
                    <p className="text-2xl font-bold text-red-600">
                      {recordsQuery.data.items.filter((u: any) => u.status === "inactive").length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#009A44]/10">
                    <Shield className="h-5 w-5 text-[#009A44]" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Admins</p>
                    <p className="text-2xl font-bold">
                      {recordsQuery.data.items.filter((u: any) => u.role === "Admin").length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <DataTable
          isFetching={recordsQuery.isFetching}
          defaultColumnVisibility={{
            createdAt: false,
          }}
          isLoading={recordsQuery.status === "loading"}
          data={recordsQuery?.data?.items || []}
          columns={columns}
          onSearch={(e) => {
            setSearchText(e);
          }}
          sorting={sorting}
          setSorting={setSorting}
          pageCount={recordsQuery?.data?.totalPages}
          setPagination={setPagination}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setColumnFilters={setColumnFilters}
          columnFilters={columnFilters}
          facets={[
            {
              accessorKey: "status",
              title: "Status",
              options: [
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ],
            },
            {
              accessorKey: "role",
              title: "Role",
              options: [
                { label: "President", value: "President" },
                { label: "Admin", value: "Admin" },
                { label: "Technical Support", value: "Technical Support" },
                { label: "Secretary", value: "Secretary" },
                { label: "Accountant", value: "Accountant" },
                { label: "Member", value: "Member" },
              ],
            },
          ]}
        />
      </div>

      <UserForm
        isOpen={newRecordModal.isOpen || Boolean(recordToEdit)}
        setIsOpen={(e) => {
          newRecordModal.setisOpen(e);
          if (!e) {
            setRecordToEdit(undefined);
          }
        }}
        refetch={recordsQuery.refetch}
        record={recordToEdit}
      />
    </>
  );
}
